/**
 * ventanaAseosAbiertos.ts — la VENTANA de aseos abiertos que ve (y puede tomar) una aseadora.
 *
 * Round 2026-10-28, pedido del dueño: una aseadora solo ve y toma los aseos abiertos de los
 * próximos N días (hoy+N inclusive; 14 por defecto); los más lejanos siguen abiertos para el admin
 * y entran solos a su lista al acercarse. La regla CANÓNICA vive en el backend
 * (`backend/src/utils/ventanaAseosAbiertos.js`) y el servidor ya no entrega lo que queda fuera:
 * esta copia es solo defensa (aseos que llegan por WebSocket, una pestaña abierta varios días) y
 * para el texto de la nota.
 *
 * N es CONFIGURABLE (ajuste general `ventanaAseosAbiertosDias`, 1..90, en /configuracion). El
 * frontend NO guarda el número: lo recibe del servidor en cada `GET /api/aseos`
 * (`ventanaAseosAbiertosDias`) y lo pasa como parámetro `dias`. La constante de acá es solo el
 * valor por defecto (si el servidor no lo manda); `ventanaAseosAbiertos.test.ts` falla si no
 * coincide con la del backend.
 *
 * Solo hay cota superior: un abierto de ayer que nadie tomó sigue visible. Un abierto sin fecha
 * no se esconde. Las fechas son strings `YYYY-MM-DD` y la aritmética es de calendario (UTC puro),
 * sin pasar por la zona del dispositivo; «hoy» es el de Chile (`hoyChile`).
 */
import { hoyChile } from "./fechaChile";

/** Valor por defecto de la ventana (días). El vigente lo manda el servidor. */
export const VENTANA_ASEOS_ABIERTOS_DIAS = 14;

/** Rango válido del ajuste (días enteros). */
export const VENTANA_ASEOS_MIN_DIAS = 1;
export const VENTANA_ASEOS_MAX_DIAS = 90;

/**
 * Lo que manda el servidor -> días válidos (entero 1..90); cualquier otra cosa -> el default.
 * Así un payload viejo o corrupto nunca esconde aseos ni rompe la pantalla.
 */
export const normalizarVentanaDias = (valor: unknown): number => {
  const n = typeof valor === "string" && valor.trim() !== "" ? Number(valor) : valor;
  if (
    typeof n === "number" &&
    Number.isInteger(n) &&
    n >= VENTANA_ASEOS_MIN_DIAS &&
    n <= VENTANA_ASEOS_MAX_DIAS
  ) {
    return n;
  }
  return VENTANA_ASEOS_ABIERTOS_DIAS;
};

/** Suma días a un `YYYY-MM-DD` con aritmética de calendario pura (sin zona horaria). */
const sumarDias = (iso: string, dias: number): string => {
  const [a, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(a, m - 1, d + dias)).toISOString().slice(0, 10);
};

/** Último día (inclusive) que una aseadora ve de un aseo abierto: hoy + ventana. */
export const limiteVentanaAbiertos = (
  hoy: string = hoyChile(),
  dias: number = VENTANA_ASEOS_ABIERTOS_DIAS,
): string => sumarDias(hoy, dias);

/**
 * ¿Un aseo abierto con esta `fecha_inicio` está dentro de la ventana?
 * Sin fecha válida -> `true` (no se esconde un dato roto), igual que el backend.
 */
export const aseoAbiertoEnVentana = (
  fechaInicio: string | null | undefined,
  hoy: string = hoyChile(),
  dias: number = VENTANA_ASEOS_ABIERTOS_DIAS,
): boolean => {
  const fecha = typeof fechaInicio === "string" ? fechaInicio.slice(0, 10) : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) return true;
  // Strings `YYYY-MM-DD`: la comparación lexicográfica ES la cronológica.
  return fecha <= limiteVentanaAbiertos(hoy, dias);
};

/** Texto de la nota discreta sobre la lista de abiertos de la aseadora. */
export const textoVentanaAbiertos = (dias: number = VENTANA_ASEOS_ABIERTOS_DIAS): string =>
  `Solo se muestran los aseos ${
    dias === 1 ? "del próximo día" : `de los próximos ${dias} días`
  }; los más lejanos se habilitan a medida que se acercan.`;
